/**
 * EvalViewModeSwitch — persistent 2D / 3D mode switch for the results view.
 *
 * WHY THIS EXISTS (regression fix 2026-07-27):
 *   The 2D/3D toggle previously lived inside EvalOverlayControlBar, which is
 *   rendered INSIDE the video-compare grid. That grid is collapsed to
 *   `invisible h-0` when viewMode === '3d', so the toggle disappeared the moment
 *   3D was engaged — leaving no way back to 2D. This control is rendered ABOVE
 *   the grid (a sibling of both the grid and the 3D viewer), so it is always
 *   visible in both modes.
 *
 * STYLE: report theme tokens (--results-*) so it matches the redesigned page,
 *   not the legacy dark-base chrome. Segmented control: "2D Video | 3D Skeleton".
 */

import React from 'react';

export interface EvalViewModeSwitchProps {
  viewMode: '2d' | '3d';
  onChange: (mode: '2d' | '3d') => void;
  className?: string;
}

function CubeGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M6 1L10.5 3.5V8.5L6 11L1.5 8.5V3.5L6 1Z M6 1V6 M6 6L10.5 3.5 M6 6L1.5 3.5 M6 6V11"
        stroke="currentColor"
        strokeWidth="0.9"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function FrameGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <rect x="1.5" y="2.5" width="9" height="7" rx="1" stroke="currentColor" strokeWidth="0.9" />
      <path d="M4.6 6L5.9 7.2L8 4.8" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function EvalViewModeSwitch({
  viewMode,
  onChange,
  className,
}: EvalViewModeSwitchProps): React.JSX.Element {
  const segBase =
    'inline-flex items-center gap-1.5 px-3.5 py-1.5 text-[12px] font-semibold ' +
    'transition-colors duration-150 select-none cursor-pointer focus:outline-none ' +
    'focus-visible:ring-1 focus-visible:ring-white/30';

  return (
    <div
      data-testid="eval-view-mode-switch"
      role="group"
      aria-label="View mode"
      className={['flex items-center justify-end px-4 pt-3', className ?? ''].join(' ')}
    >
      <div
        className="inline-flex rounded-[var(--results-radius-pill)] overflow-hidden border"
        style={{ borderColor: 'color-mix(in srgb, var(--results-accent-report) 45%, transparent)' }}
      >
        <button
          type="button"
          data-testid="view-mode-2d"
          onClick={() => onChange('2d')}
          aria-pressed={viewMode === '2d'}
          className={segBase}
          style={
            viewMode === '2d'
              ? {
                  background: 'color-mix(in srgb, var(--results-accent-report) 22%, transparent)',
                  color: 'var(--results-accent-report)',
                }
              : { background: 'transparent', color: 'var(--results-text-dim)' }
          }
        >
          <FrameGlyph />
          2D Video
        </button>
        <button
          type="button"
          data-testid="view-mode-3d"
          onClick={() => onChange('3d')}
          aria-pressed={viewMode === '3d'}
          className={segBase}
          style={
            viewMode === '3d'
              ? {
                  background: 'color-mix(in srgb, var(--results-accent-report) 22%, transparent)',
                  color: 'var(--results-accent-report)',
                }
              : { background: 'transparent', color: 'var(--results-text-dim)' }
          }
        >
          <CubeGlyph />
          3D Skeleton
        </button>
      </div>
    </div>
  );
}
